'use client';

import { Modal } from '@material-ui/core';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ComponentProps, FC, useCallback, useMemo, useState } from 'react';

import { Button } from 'src/general/components/Button/Button';
import { Counter, counterWrapperStyle } from 'src/general/components/Counter/Counter';
import { Image } from 'src/general/components/Image/Image';
import { PromoPill } from 'src/general/components/PromoPill/PromoPill';
import { useModal } from 'src/hooks/useModal';
import { FlexibleSection } from '../flexibleContent.type';
import { getSectionClasses } from '../getSectionClasses.helper';
import styles from './SectionLayout.module.scss';

const ReactMarkdown = dynamic(() => import('react-markdown'), { ssr: false });
const CarouselSectionLayout = dynamic(() => import('./CarouselSectionLayout').then((mod) => mod.CarouselSectionLayout));

type SectionLayoutItem = {
  title: string;
  content: string;
  image: string;
  cta?: {
    label: string;
  } & (
    | {
        type: 'link';
        href: string;
      }
    | {
        type: 'openModal';
        modal: {
          title: string;
          image: string;
          number?: number;
          carouselContent: { subTitle?: string; subText: string }[];
        };
      }
  );
};

interface Props extends FlexibleSection {
  sectionData: {
    title?: string;
    items: SectionLayoutItem[];
    isHorizontal?: boolean;
    isHalfHeight?: boolean;
    isNumbersColorChange?: boolean;
    className?: string;
    tagLabel?: string;
    tagIcon?: string;
    isButtonsAligned?: boolean;
    isHiddenNumber?: boolean;
    isCallout?: boolean;
  };
}

export const SectionLayout: FC<Props> = ({
  sectionData: {
    title,
    items,
    isHorizontal,
    isHalfHeight,
    isButtonsAligned,
    isNumbersColorChange,
    isHiddenNumber,
    className,
    tagLabel,
    tagIcon,
    isCallout,
  },
  sectionBackground,
}) => {
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const { isVisible: modalOpen, open: openModal, close: closeModal } = useModal();

  const counterVariant: ComponentProps<typeof Counter>['variant'] = isNumbersColorChange ? 'secondary' : 'primary';

  const carouselData = useMemo(
    () =>
      items.map(({ cta }) => (cta?.type === 'openModal' ? cta?.modal : { title: '', image: '', carouselContent: [] })),
    [items],
  );

  const openModalTo = useCallback(
    (index: number) => {
      openModal();
      setActiveSlide(index);
    },
    [openModal],
  );

  return (
    <section className={classNames(styles.wrapper, getSectionClasses(sectionBackground))}>
      <div
        className={classNames(styles.root, className, {
          [styles.callOutSection]: isCallout,
        })}
      >
        {tagLabel && (
          <div className={styles.tagLabelContainer}>
            <PromoPill
              promoPillData={[
                {
                  key: tagLabel,
                  copy: tagLabel,
                  icon: tagIcon,
                  backgroundColor: '#EBF2F5',
                  color: 'rgba(21, 13, 44, 0.88)',
                },
              ]}
              className={styles.tagLabel}
            />
          </div>
        )}
        <header className={styles.header}>
          <h2 className={classNames('c-fw-bold', 'c-fs-h4')}>{title}</h2>
        </header>
        {items.some((item) => item.cta?.type === 'openModal') && (
          <Modal open={modalOpen}>
            <CarouselSectionLayout
              activeSlide={activeSlide}
              onClose={closeModal}
              counterVariant={counterVariant}
              carouselData={carouselData as any}
            />
          </Modal>
        )}
        <ol className={classNames(styles.container, counterWrapperStyle)}>
          {items.map(({ image, title, cta, content }, index) => (
            <li
              key={`${title}-${image}`}
              className={classNames({
                [styles.horizontalContentContainer]: isHorizontal,
                [styles.buttonAlign]: isButtonsAligned,
              })}
            >
              <div
                className={classNames(styles.imgContainer, {
                  [styles.horizontalImgContainer]: isHorizontal,
                  [styles.halfHeightImgContainer]: isHalfHeight,
                  [styles.imgLink]: cta?.type === 'openModal',
                })}
              >
                {isHiddenNumber === false && <Counter variant={counterVariant} />}
                <Image
                  src={image}
                  className={classNames(styles.img, isCallout ? styles.imgContain : styles.imgCover)}
                  alt=""
                  width={isCallout ? 240 : 522}
                  height={isCallout ? 100 : 522}
                  onClick={cta?.type === 'openModal' ? () => openModalTo(index) : undefined}
                />
              </div>
              <div className={styles.content}>
                <h3
                  className={classNames(styles.contentTitle, 'c-fw-bold', {
                    ['c-fs-h6']: isCallout,
                    ['c-fs-h5']: !isCallout,
                  })}
                >
                  {title}
                </h3>
                <ReactMarkdown className={classNames(styles.text, 'c-fw-normal', 'c-fs-body2')}>
                  {content}
                </ReactMarkdown>
                <div className={styles.buttonContainer}>
                  {cta?.type === 'link' && (
                    <Link prefetch={false} href={cta.href} passHref legacyBehavior>
                      <Button variant="secondary" size="small">
                        {cta.label}
                      </Button>
                    </Link>
                  )}
                  {cta?.type === 'openModal' && (
                    <Button variant="secondary" size="small" onClick={() => openModalTo(index)}>
                      {cta.label}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
